import { createFileRoute, useRouteContext } from "@tanstack/react-router";
import { useMemo, type JSX } from "react";

import { graphql } from "@repo/graphql-types";
import Left from "@repo/icons/default/Left";
import Question from "@repo/icons/default/Question";
import {
  CpsAlertV2Description,
  CpsAlertV2IconArea,
  CpsAlertV2Root,
  CpsAlertV2TextArea,
} from "corpus";

import {
  HeaderButton,
  HeaderRoot,
  HeaderTitle,
} from "@/components/ui/layout/header/header";
import { UnidadeCard } from "@/components/locations/unidade-card";
import { Page } from "@/components/ui/layout/page/page";
import { useGraphQL } from "@/hooks/use-graphql";
import { useZendeskLogin } from "@/hooks/use-zendesk-login";

// biome-ignore lint/plugin/route-file-exports: predates the rule; move it out
export const UnidadesQuery = graphql(`
  query Unidades {
    unidades(
      where: { ativo: { _eq: true }, codUnidade: { _nin: [1, 6] } }
      order_by: { nome: asc }
    ) {
      codUnidade
      ...UnidadeCardFragment
    }
  }
`);

const LocationsPage = (): JSX.Element => {
  const { flags } = useRouteContext({ from: "__root__" });

  const shouldDisplayChatbotAdditionalInfoAlert = useMemo(
    () => flags["exibe-botoes-do-chatbot-zendesk-no-app"]?.settings ?? false,
    [flags],
  );

  const { loginWithZendesk } = useZendeskLogin();

  const queryResult = useGraphQL(UnidadesQuery, {});
  const unidades = queryResult.data?.unidades;

  // Estado vazio
  if (!unidades) {
    return (
      <>
        <HeaderRoot>
          <HeaderButton icon={Left} align="start" />
          <HeaderTitle title="Unidades" align="center" />
        </HeaderRoot>
        <Page>
          <div className="text-center py-8">
            <div className="text-gray-400 mb-2">🏢</div>
            <p className="text-gray-600">Nenhuma unidade encontrada</p>
          </div>
        </Page>
      </>
    );
  }

  // Estado com dados
  return (
    <>
      <HeaderRoot>
        <HeaderButton icon={Left} align="start" />
        <HeaderTitle title="Unidades" align="center" />
      </HeaderRoot>
      <Page>
        <div className="flex flex-col">
          {/* Header da página */}
          <div className="mb-4">
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Nossas Unidades</h1>
            <p className="text-gray-600 text-sm mb-2">
              Encontre informações sobre acesso, estacionamento, horários especiais e
              muito mais.
            </p>
            {/* Informações adicionais */}
            {shouldDisplayChatbotAdditionalInfoAlert ? (
              <CpsAlertV2Root type="info">
                <CpsAlertV2IconArea>
                  <Question className="fill-success-700 self-center" size={24} />
                </CpsAlertV2IconArea>
                <CpsAlertV2TextArea>
                  <CpsAlertV2Description>
                    Não encontrou o que precisa? Fale com nossa{" "}
                    <button
                      type="button"
                      onClick={loginWithZendesk}
                      className="underline text-success-700"
                    >
                      assistente virtual
                    </button>
                  </CpsAlertV2Description>
                </CpsAlertV2TextArea>
              </CpsAlertV2Root>
            ) : null}
          </div>
          {/* Lista de unidades */}
          <div className="space-y-4">
            {unidades.map((unidade) => (
              <UnidadeCard key={unidade.codUnidade} data={unidade} />
            ))}
          </div>
        </div>
      </Page>
    </>
  );
};

export const Route = createFileRoute("/locations/")({
  component: LocationsPage,
});
